
import { useState } from 'react';
import { useToast } from "@/hooks/use-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DevotionalFormValues } from "../types";
import { Json } from "@/integrations/supabase/types";

interface UseDevotionalCompletionProps {
  userId: string;
  devotionalId: number;
  points: number;
  onComplete?: () => void;
}

export function useDevotionalCompletion({
  userId,
  devotionalId,
  points,
  onComplete
}: UseDevotionalCompletionProps) {
  const [showConfetti, setShowConfetti] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { mutate: handleCompletion, isPending: isSubmitting } = useMutation({
    mutationFn: async (values: DevotionalFormValues) => {
      const completedActions = values.completed_actions.map(action => ({
        completed: action.completed,
        ...(action.text && { text: action.text }) // Only include text if it exists
      })) as Json;

      // First check if a progress record already exists
      const { data: existingProgress } = await supabase
        .from('devotional_progress')
        .select('id, completed_at')
        .eq('user_id', userId)
        .eq('devotional_id', devotionalId)
        .maybeSingle();

      if (existingProgress?.completed_at) {
        throw new Error("Already completed");
      }

      // If there's no completed progress, update or insert
      const { error } = await supabase
        .from('devotional_progress')
        .upsert({
          user_id: userId,
          devotional_id: devotionalId,
          completed_prayer: values.completed_prayer,
          completed_reflection: values.completed_reflection,
          completed_actions: completedActions,
          completed_at: new Date().toISOString(),
          points_earned: values.completed_prayer && values.completed_reflection ? points : 0
        }, {
          onConflict: 'user_id,devotional_id'
        });

      if (error) throw error;
      return values;
    },
    onSuccess: () => {
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 5000);
      
      toast({
        title: "🎉 Points Earned!",
        description: `You've earned ${points} points for completing today's devotional!`,
      });

      // Invalidate all relevant queries and wait for them to settle
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['devotionalProgress'] }),
        queryClient.invalidateQueries({ queryKey: ['devotional'] }),
        queryClient.invalidateQueries({ queryKey: ['userPoints'] })
      ]).then(() => {
        onComplete?.();
      });
    },
    onError: (error: Error) => {
      if (error.message === "Already completed") {
        toast({
          title: "Already Completed",
          description: "You've already completed this devotional",
        });
      } else {
        console.error('Error saving progress:', error);
        toast({
          title: "Error",
          description: "Failed to save progress",
          variant: "destructive",
        });
      }
    }
  });

  return {
    isSubmitting,
    showConfetti,
    handleCompletion
  };
}
